'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Game } from '@/lib/games';
import type { GameVideo } from '@/lib/gameVideos';

/**
 * The game's tutorial, BIG: a recorded attract run (scripts/record-game-video.mjs)
 * in a near-full-screen player — a thick hot gradient across the top, the
 * video at 16:10 filling the width, play/pause, a scrubber with the story's
 * chapter ticks, mute and replay, chapter chips that jump to each beat, and a
 * button that closes this and launches the game.
 *
 * Rendered through a portal on document.body, so the shelf dialog it opens
 * from (and its tile's play button) never clip or catch it. Sound starts off.
 */
export default function GameTutorialDialog({
  game,
  video,
  playLabel,
  onPlay,
  onClose,
}: {
  game: Game;
  video: GameVideo;
  /** The tile's own action ("Play Iron Talon" / "Buy Iron Talon"); none = no button. */
  playLabel?: string;
  onPlay?: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(video.duration || 0);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(true);
  const [ended, setEnded] = useState(false);
  const chapters = video.chapters || [];
  const at = chapters.reduce((k, c, i) => (t + 0.05 >= c.t ? i : k), 0);

  // Esc closes; Tab stays inside the dialog; focus returns to where it came from.
  useEffect(() => {
    const back = document.activeElement as HTMLElement | null;
    closeBtn.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); onClose(); return; }
      if (e.key === 'Tab' && box.current) {
        const f = Array.from(box.current.querySelectorAll<HTMLElement>('button, [href], input, [tabindex]:not([tabindex="-1"])')).filter((el) => !el.hasAttribute('disabled'));
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
      if (e.key === ' ' && document.activeElement === box.current) { e.preventDefault(); toggle(); }
    };
    // capture: the shelf dialog underneath also listens for Esc, and must not close with this one
    window.addEventListener('keydown', onKey, true);
    return () => { window.removeEventListener('keydown', onKey, true); back?.focus?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The shelf closes on a mousedown outside itself (a document listener); this
  // dialog lives on <body>, outside it, so its presses must not get that far.
  useEffect(() => {
    const el = root.current; if (!el) return;
    const stop = (e: Event) => e.stopPropagation();
    el.addEventListener('mousedown', stop); el.addEventListener('touchstart', stop, { passive: true });
    return () => { el.removeEventListener('mousedown', stop); el.removeEventListener('touchstart', stop); };
  }, []);

  // The clock for the scrubber, read every frame while open.
  useEffect(() => {
    let raf = 0;
    const tick = () => { const v = ref.current; if (v) { setT(v.currentTime); if (v.duration) setDur(v.duration); } raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const toggle = useCallback(() => {
    const v = ref.current; if (!v) return;
    if (v.paused || v.ended) { if (v.ended) v.currentTime = 0; v.play().catch(() => {}); } else v.pause();
  }, []);
  const seek = (s: number) => { const v = ref.current; if (!v) return; v.currentTime = Math.max(0, Math.min(s, (v.duration || dur) - 0.05)); if (v.paused) v.play().catch(() => {}); };
  const replay = () => seek(0);
  const scrub = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    seek(((e.clientX - r.left) / r.width) * (dur || 1));
  };
  const D = dur || 1;

  return createPortal(
    // React bubbles portal events up ITS tree — into the tile's play button this
    // opened from — so nothing that happens in here may travel further.
    <div ref={root} className="fixed inset-0 z-[120] flex items-center justify-center p-0 sm:p-6" role="presentation"
      onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}
      onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()}>
      <div className="absolute inset-0 bg-ink-950/70 backdrop-blur-md dark:bg-black/75" onClick={onClose} aria-hidden />
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-label={`${game.name} tutorial`}
        tabIndex={-1}
        className="gt-tut relative z-10 flex h-[100dvh] w-full flex-col overflow-hidden bg-white shadow-2xl outline-none
                   sm:h-auto sm:max-h-[96vh] sm:w-[var(--tut-w)] sm:rounded-2xl sm:ring-1 sm:ring-black/10
                   dark:bg-ink-900 sm:dark:ring-white/10"
        // as wide as 85vw / 1280px allow, but never so tall that the controls fall below the fold
        style={{ ['--tut-w' as string]: 'min(85vw, 1280px, calc((96vh - 210px) * 1.7778))' }}
      >
        {/* the power stripe */}
        <div aria-hidden className="relative h-2 w-full shrink-0 overflow-hidden bg-gradient-to-r from-amber-300 via-orange-500 to-red-700 shadow-[0_0_24px_rgba(249,115,22,.65)]">
          <span className="gt-tut-shine absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/80 to-transparent" />
        </div>

        <div className="flex shrink-0 items-center gap-3 px-4 py-2.5 sm:px-5 sm:py-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-extrabold tracking-tight text-ink-900 sm:text-xl dark:text-white">{game.name}</p>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-orange-600 dark:text-orange-400">Tutorial · how to win</p>
          </div>
          <button
            ref={closeBtn}
            type="button"
            onClick={onClose}
            aria-label="Close tutorial"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-ink-200 text-ink-600 transition hover:bg-ink-100 hover:text-ink-900
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 dark:border-ink-700 dark:text-ink-300 dark:hover:bg-ink-800 dark:hover:text-white"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {/* the video, 16:10, the full width (on a phone the player sits mid-screen) */}
          <div className="relative aspect-video w-full shrink-0 bg-black max-sm:mt-auto">
            <video
              ref={ref}
              className="absolute inset-0 h-full w-full cursor-pointer object-contain"
              muted={muted}
              playsInline
              autoPlay
              preload="auto"
              poster={`/games/videos/${game.id}.jpg`}
              onClick={toggle}
              onPlay={() => { setPaused(false); setEnded(false); }}
              onPause={() => setPaused(true)}
              onEnded={() => setEnded(true)}
              aria-label={`${game.name} tutorial video`}
            >
              <source src={`/games/videos/${game.id}.webm`} type="video/webm" />
              <source src={`/games/videos/${game.id}.mp4`} type="video/mp4" />
            </video>
            {(paused || ended) && (
              <button type="button" onClick={ended ? replay : toggle} aria-label={ended ? 'Watch again' : 'Play'}
                className="absolute left-1/2 top-1/2 grid h-16 w-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-gradient-to-br from-amber-300 via-orange-500 to-red-600 text-white shadow-[0_0_40px_rgba(249,115,22,.6)] ring-4 ring-white/20">
                {ended
                  ? <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></svg>
                  : <svg viewBox="0 0 24 24" className="ml-1 h-7 w-7" fill="currentColor"><path d="M7 4.5v15l12.5-7.5z" /></svg>}
              </button>
            )}
          </div>

          {/* controls: play/pause · scrubber with chapter ticks · time · mute · replay */}
          <div className="flex items-center gap-2 bg-ink-950 px-3 py-2 text-white sm:gap-3 sm:px-4">
            <CtlBtn label={paused || ended ? 'Play' : 'Pause'} onClick={ended ? replay : toggle}>
              {paused || ended
                ? <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M7 4.5v15l12.5-7.5z" /></svg>
                : <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M6 4h4v16H6zM14 4h4v16h-4z" /></svg>}
            </CtlBtn>
            <div
              role="slider"
              tabIndex={0}
              aria-label="Seek"
              aria-valuemin={0}
              aria-valuemax={Math.round(D)}
              aria-valuenow={Math.round(t)}
              aria-valuetext={`${fmt(t)} of ${fmt(D)}${chapters[at] ? ' · ' + chapters[at].label : ''}`}
              onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); scrub(e); }}
              onPointerMove={(e) => { if (e.buttons & 1) scrub(e); }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight') { e.preventDefault(); seek(t + 5); }
                if (e.key === 'ArrowLeft') { e.preventDefault(); seek(t - 5); }
              }}
              className="group relative h-6 min-w-0 flex-1 cursor-pointer touch-none focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
            >
              <span className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-white/15" />
              <span className="absolute left-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-gradient-to-r from-amber-300 via-orange-500 to-red-600" style={{ width: `${(t / D) * 100}%` }} />
              {chapters.slice(1).map((c) => (
                <span key={c.label} aria-hidden className="absolute top-1/2 h-3 w-0.5 -translate-y-1/2 rounded bg-white/70" style={{ left: `${(c.t / D) * 100}%` }} />
              ))}
              <span aria-hidden className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow ring-2 ring-orange-500" style={{ left: `${(t / D) * 100}%` }} />
            </div>
            <span className="w-[5.5rem] shrink-0 text-right font-mono text-[11px] tabular-nums text-white/70 max-sm:hidden">{fmt(t)} / {fmt(D)}</span>
            <CtlBtn label={muted ? 'Sound on' : 'Mute'} onClick={() => setMuted((m) => !m)}>
              {muted
                ? <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H2v6h4l5 4z" fill="currentColor" /><path d="m22 9-6 6M16 9l6 6" /></svg>
                : <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H2v6h4l5 4z" fill="currentColor" /><path d="M15.5 8.5a5 5 0 0 1 0 7M19 5a10 10 0 0 1 0 14" /></svg>}
            </CtlBtn>
            <CtlBtn label="Watch from the start" onClick={replay}>
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></svg>
            </CtlBtn>
          </div>

          {/* chapters + the way in */}
          <div className="flex flex-col gap-3 px-4 py-3 max-sm:mb-auto max-sm:gap-4 max-sm:pb-[max(1rem,env(safe-area-inset-bottom))] sm:flex-row sm:items-center sm:px-5 sm:py-4">
            <div className="flex min-w-0 flex-wrap content-start gap-1.5 sm:flex-1" role="group" aria-label="Chapters">
              {chapters.map((c, i) => (
                <button
                  key={c.label}
                  type="button"
                  onClick={() => seek(c.t)}
                  aria-current={i === at ? 'step' : undefined}
                  className={
                    'rounded-full border px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ' +
                    (i === at
                      ? 'border-orange-500 bg-gradient-to-r from-amber-400 to-orange-500 text-ink-950 shadow-[0_0_14px_rgba(249,115,22,.45)]'
                      : 'border-ink-200 text-ink-600 hover:border-orange-400 hover:text-ink-900 dark:border-ink-700 dark:text-ink-300 dark:hover:text-white')
                  }
                >
                  <span className="mr-1 opacity-60">{i + 1}</span>{c.label}
                </button>
              ))}
            </div>
            {onPlay && playLabel && (
              <button
                type="button"
                onClick={() => { onClose(); onPlay(); }}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 via-orange-500 to-red-600 px-5 py-2.5 text-sm font-extrabold uppercase tracking-wide text-white
                           shadow-[0_6px_24px_rgba(234,88,12,.45)] transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-ink-900"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M7 4.5v15l12.5-7.5z" /></svg>
                {playLabel}
              </button>
            )}
          </div>
        </div>
      </div>
      <style>{`
        .gt-tut-shine { animation: gt-tut-shine 2.8s ease-in-out infinite; }
        @keyframes gt-tut-shine { 0% { transform: translateX(0); } 60%, 100% { transform: translateX(420%); } }
        @media (prefers-reduced-motion: reduce) { .gt-tut-shine { animation: none; } }
      `}</style>
    </div>,
    document.body,
  );
}

function CtlBtn({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} title={label}
      className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-white/90 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500">
      {children}
    </button>
  );
}

function fmt(s: number) {
  s = Math.max(0, Math.floor(s));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
